import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Compass, BookOpen, Users, TrendingUp, Shield, Brain, GraduationCap, Building2, LineChart, Briefcase, FileSearch } from 'lucide-react';
import TerrainBackground from '@/components/assessment/TerrainBackground';
import { usePortal } from '@/contexts/PortalContext';
import { useAuth } from '@/contexts/AuthContext';
import { track } from '@/lib/analytics';

const features = [
  { icon: Brain, title: 'Grounded in Real Research', desc: 'Built on Holland\'s RIASEC framework, federal O*NET occupational data, and Bureau of Labor Statistics projections' },
  { icon: BookOpen, title: 'School-Specific', desc: 'Recommends YOUR school\'s actual courses, clubs, and extracurriculars' },
  { icon: TrendingUp, title: 'Future-Proof', desc: 'AI-resilience scoring informed by World Economic Forum and McKinsey future-of-work research' },
  { icon: Users, title: 'Counselor-Ready', desc: 'Export talking points and activities lists for counselor meetings' },
  { icon: Shield, title: 'Private & Secure', desc: 'Your data stays yours. WCAG accessible. FERPA compliant.' },
  { icon: Compass, title: 'Three Clear Paths', desc: 'Real careers matched to your strengths from 900+ O*NET occupations' },
];

// Research anchors. These are the actual frameworks the assessment engine
// already uses. Surfacing them here turns "is this a real test?" into
// "I recognize that framework" for counselors, parents, and administrators.
// RIASEC is taught in every career-counselor masters program; O*NET is the
// U.S. Department of Labor's occupational database; BLS, WEF, and McKinsey
// underpin the labor-market projections in the AI resilience scoring.
const researchAnchors = [
  {
    icon: GraduationCap,
    name: 'Holland\'s RIASEC',
    detail: 'Vocational psychology framework (Holland, 1959). Six interest categories. Taught in every career-counselor degree program.',
    year: '1959 → today',
  },
  {
    icon: Building2,
    name: 'O*NET',
    detail: 'U.S. Department of Labor occupational database. 900+ occupations with skills, tasks, growth, and wages.',
    year: 'Updated 2026',
  },
  {
    icon: LineChart,
    name: 'BLS Projections',
    detail: 'Bureau of Labor Statistics 2024-2034 employment outlook used in every path\'s growth score.',
    year: '2024-2034',
  },
  {
    icon: Briefcase,
    name: 'WEF Future of Jobs',
    detail: 'World Economic Forum disruption analysis. Powers the AI resilience score on every recommended path.',
    year: 'Annual',
  },
  {
    icon: FileSearch,
    name: 'McKinsey Automation',
    detail: 'McKinsey Global Institute task-automation modeling. Filters paths that AI is likely to redefine.',
    year: '2023-2025',
  },
];

const PortalLanding = () => {
  const { config, basePath, label } = usePortal();
  const { user } = useAuth();
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();

  const handleStart = () => {
    track({ event: 'assessment_started', props: { edition: config.edition } });
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    navigate(user ? `${basePath}/assessment` : `${basePath}/auth`);
  };

  const fadeUp = prefersReducedMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.3 } }
    : { initial: { opacity: 0, y: 24 }, animate: { opacity: 1, y: 0 } };

  return (
    <div className="min-h-screen bg-journey-atmosphere relative overflow-hidden">
      <TerrainBackground />
      <div className="noise-overlay" />

      {/* Hero */}
      <main id="main-content" className="relative z-10">
        <section className="min-h-[85vh] flex flex-col items-center justify-center px-6 py-20">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            {/* Badge */}
            <motion.div
              {...fadeUp}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/8 border border-primary/15"
            >
              <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
              <span className="text-xs font-semibold text-primary tracking-wide">{label} Edition</span>
            </motion.div>

            {/* Heading */}
            <motion.h1
              {...fadeUp}
              transition={{ delay: 0.2, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-display font-bold text-foreground leading-[1.1] tracking-tight"
            >
              Discover where
              <br />
              <span className="text-gradient-teal">you're headed</span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              {...fadeUp}
              transition={{ delay: 0.35, duration: 0.6 }}
              className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto leading-relaxed"
            >
              {config.tagline}. Powered by real career data, personalized for you.
            </motion.p>

            {/* CTA */}
            <motion.div
              {...fadeUp}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4"
            >
              <button
                onClick={handleStart}
                className="btn-primary text-base px-8 py-4 rounded-2xl glow-teal"
              >
                Discover Your True Path
                <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </button>
            </motion.div>

            {/* Meta */}
            <motion.p
              {...fadeUp}
              transition={{ delay: 0.65, duration: 0.5 }}
              className="text-xs text-muted-foreground/60"
            >
              Takes 5-10 minutes · Grounded in vocational psychology research and federal labor data · Free
            </motion.p>
          </div>
        </section>

        {/* Research anchoring */}
        <section className="px-6 pb-20" aria-labelledby="research-heading">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-center space-y-3 mb-10"
            >
              <p className="text-xs font-semibold tracking-[0.18em] uppercase text-primary/80">
                Grounded in research
              </p>
              <h2 id="research-heading" className="text-2xl md:text-3xl font-display font-bold text-foreground">
                Built on <span className="text-gradient-teal">60 years of vocational psychology</span> and federal labor data.
              </h2>
              <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                TruePath does not invent its own framework. The assessment maps your answers to peer-reviewed interest categories, then matches to live occupational data the U.S. government and global research institutions publish.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {researchAnchors.map((anchor, i) => (
                <motion.div
                  key={anchor.name}
                  initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06, duration: 0.4 }}
                  className="glass-card p-4 space-y-2 text-left"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <anchor.icon className="w-4 h-4 text-primary" aria-hidden="true" />
                    </div>
                    <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground/70">{anchor.year}</span>
                  </div>
                  <h3 className="font-display font-bold text-foreground text-sm leading-tight">{anchor.name}</h3>
                  <p className="text-[11px] text-muted-foreground leading-relaxed">{anchor.detail}</p>
                </motion.div>
              ))}
            </div>

            <motion.p
              initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center text-xs text-muted-foreground/70 mt-8"
            >
              Curious how it works under the hood? Read <button onClick={() => navigate(`${basePath}/about`)} className="text-primary hover:underline">our methodology</button>.
            </motion.p>
          </div>
        </section>

        {/* Features grid */}
        <section className="px-6 pb-24">
          <div className="max-w-5xl mx-auto">
            <motion.div
              {...fadeUp}
              transition={{ delay: 0.2, duration: 0.6 }}
              viewport={{ once: true }}
              className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {features.map((feature, i) => (
                <motion.div
                  key={i}
                  initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08, duration: 0.5 }}
                  className="glass-card p-6 space-y-3 group"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/8 flex items-center justify-center group-hover:bg-primary/12 transition-colors">
                    <feature.icon className="w-5 h-5 text-primary" aria-hidden="true" />
                  </div>
                  <h3 className="font-display font-bold text-foreground text-sm">{feature.title}</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">{feature.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Path preview */}
        <section className="px-6 pb-24">
          <div className="max-w-3xl mx-auto text-center space-y-8">
            <motion.h2
              initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl font-display font-bold text-foreground"
            >
              Three paths, one destination: <span className="text-gradient-teal">yours</span>
            </motion.h2>

            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { title: 'The Traditional Path', desc: 'Strong fundamentals with a clear direction toward a specific career.' },
                { title: 'The Hybrid Path', desc: 'Balance structure with exploration. Build skills across two domains.' },
                { title: 'The Bold Path', desc: 'Chart your own course. Entrepreneurship, creativity, and independence.' },
              ].map((level, i) => (
                <motion.div
                  key={i}
                  initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="glass-card p-5 text-left space-y-2"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-primary-foreground ${
                    i === 0 ? 'bg-accent' : i === 1 ? 'bg-primary' : 'bg-foreground'
                  }`}>
                    {i + 1}
                  </div>
                  <h3 className="font-display font-bold text-foreground text-sm">
                    {level.title}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {level.desc}
                  </p>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <button
                onClick={handleStart}
                className="btn-primary text-base px-8 py-4 rounded-2xl"
              >
                Find Your Path
                <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </button>
            </motion.div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="relative z-10 text-center pb-10 px-6 space-y-2">
        <p className="text-xs text-muted-foreground/50">
          Grounded in vocational psychology research and federal labor data.
        </p>
        <p className="text-xs text-muted-foreground/30">By TruePath</p>
      </footer>
    </div>
  );
};

export default PortalLanding;
